import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { ref, get, set } from "firebase/database";
import { useNavigate } from "react-router-dom";
import useFile from "../hooks/useFile";
import GameForm from "../components/GameForm";
import Popup from "../components/Popup";
import Confirm from "../components/Confirm";

const OrganizerHome = () => {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGameForm, setShowGameForm] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [popup, setPopup] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });
  const [confirm, setConfirm] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    type: "warning",
  });
  const { deleteFile } = useFile();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        // Check if user is organizer
        const userRef = ref(db, `users/${user.uid}`);
        const userSnapshot = await get(userRef);

        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();

          if (userData.role !== "organizer") {
            setPopup({
              isOpen: true,
              title: "Access Denied",
              message: "Access denied. Organizer only.",
              type: "error",
            });
            navigate("/");
            return;
          }

          await fetchGames();
        }
      } catch (error) {
        setPopup({
          isOpen: true,
          title: "Load Failed",
          message: "Error loading games: " + error.message,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchGames = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const gamesRef = ref(db, `users/${user.uid}/games`);
      const snapshot = await get(gamesRef);
      if (snapshot.exists()) {
        const gamesData = snapshot.val();
        const gamesArray = await Promise.all(
          Object.entries(gamesData).map(async ([id, game]) => {
            const gameDetailsRef = ref(db, `games/${id}/details`);
            const gameDetailsSnapshot = await get(gameDetailsRef);

            if (gameDetailsSnapshot.exists()) {
              const details = gameDetailsSnapshot.val();
              return {
                id,
                gameName: game.gameName,
                createdAt: game.createdAt,
                status: game.status,
                details: details,
                banner: details.banner || "",
                description: details.description || "",
                date: details.date || "",
                location: details.location || "",
                organizerName: details.organizerName || "",
                file: details.file || "",
              };
            }

            return { id, ...game, banner: "" };
          })
        );
        gamesArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setGames(gamesArray);
      }
    } catch (error) {
      setPopup({
        isOpen: true,
        title: "Load Failed",
        message: "Error loading games: " + error.message,
        type: "error",
      });
    }
  };

  const handleEditGame = async (game) => {
    try {
      const gameDetailsRef = ref(db, `games/${game.id}/details`);
      const gameDetailsSnapshot = await get(gameDetailsRef);

      if (gameDetailsSnapshot.exists()) {
        const gameDetails = gameDetailsSnapshot.val();
        setSelectedGame({
          id: game.id,
          gameName: gameDetails.title || "",
          organizerName: gameDetails.organizerName || "",
          description: gameDetails.description || "",
          date: gameDetails.date || "",
          location: gameDetails.location || "",
          banner: gameDetails.banner || "",
          file: gameDetails.file || "",
        });
        setShowGameForm(true);
      } else {
        setPopup({
          isOpen: true,
          title: "Not Found",
          message: "Game details not found",
          type: "error",
        });
      }
    } catch (error) {
      setPopup({
        isOpen: true,
        title: "Load Failed",
        message: "Error loading game details: " + error.message,
        type: "error",
      });
    }
  };

  const handleCloseForm = () => {
    setSelectedGame(null);
    setShowGameForm(false);
  };

  const handleFormSuccess = async () => {
    await fetchGames();
  };

  const handleGameClick = (gameId) => {
    navigate(`/organizer/dashboard/${gameId}`);
  };

  const handleDeleteGame = async (gameId, gameName, gameFile) => {
    setConfirm({
      isOpen: true,
      title: "Delete Game",
      message: `Are you sure you want to delete "${gameName}"? This action cannot be undone.`,
      onConfirm: () => performDeleteGame(gameId, gameName, gameFile),
      type: "danger",
    });
  };

  const performDeleteGame = async (gameId, gameName, gameFile) => {
    try {
      const user = auth.currentUser;
      if (gameFile) {
        await deleteFile(gameFile);
      }
      
      await set(ref(db, `users/${user.uid}/games/${gameId}`), null);
      await set(ref(db, `games/${gameId}`), null);

      setGames(games.filter((game) => game.id !== gameId));
      setConfirm({ isOpen: false, title: "", message: "", onConfirm: null, type: "warning" });
      setPopup({
        isOpen: true,
        title: "Game Deleted",
        message: "Game deleted successfully!",
        type: "success",
      });
    } catch (error) {
      setPopup({
        isOpen: true,
        title: "Delete Failed",
        message: "Failed to delete game: " + error.message,
        type: "error",
      });
    }
  };



  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading your games...</p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="bi bi-trophy-fill me-2"></i>
          My Auction Games
        </h2>
        <button
          className="btn btn-primary btn-lg"
          onClick={() => {
            setSelectedGame(null);
            setShowGameForm(!showGameForm);
          }}
        >
          <i className="bi bi-plus-circle me-2"></i>
          {showGameForm ? "Hide Form" : "Create New Game"}
        </button>
      </div>

      {/* Game Form */}
      {showGameForm && (
        <div className="mb-4">
          <GameForm
            selectedGame={selectedGame}
            onClose={handleCloseForm}
            onSuccess={handleFormSuccess}
          />
        </div>
      )}

      {/* Games Grid */}
      {!showGameForm && games.length === 0 ? (
        <div className="text-center py-5">
          <i className="bi bi-inbox display-1 text-muted"></i>
          <h4 className="mt-3 text-muted">No Games Yet</h4>
          <p className="text-muted">
            Create your first auction game to get started!
          </p>
        </div>
      ) : !showGameForm ? (
        <div className="row">
          {games.map((game) => (
            <div key={game.id} className="col-md-6 col-lg-4 mb-4">
              <div
                className="card h-100 shadow-sm hover-shadow"
                style={{ transition: "all 0.3s" }}
              >
                <img
                  src={game.banner || "/banner1.jpg"}
                  className="card-img-top"
                  alt={game.gameName}
                  style={{ height: "150px", objectFit: "cover" }}
                />
                <div
                  className="card-body"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleGameClick(game.id)}
                >
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-trophy me-2 text-warning"></i>
                      {game.gameName}
                    </h5>
                    <span
                      className={`badge ${
                        game.details?.status === "live"
                          ? "bg-success"
                          : game.details?.status === "paused"
                          ? "bg-info"
                          : game.details?.status === "not_started"
                          ? "bg-primary"
                          : game.details?.status === "completed"
                          ? "bg-secondary"
                          : "bg-warning"
                      }`}
                    >
                      {game.details?.status === "not_started"
                        ? "Not Started"
                        : game.details?.status || game.status}
                    </span>
                  </div>

                  <div className="mb-3">
                    {game.organizerName && (
                      <p className="text-muted small mb-2">
                        <i className="bi bi-person-badge me-2"></i>
                        <strong>By:</strong> {game.organizerName}
                      </p>
                    )}

                    {game.description && (
                      <p className="text-muted small mb-2">
                        <i className="bi bi-file-text me-2"></i>
                        {game.description.length > 100
                          ? game.description.substring(0, 100) + "..."
                          : game.description}
                      </p>
                    )}

                    {game.date && (
                      <p className="text-muted small mb-2">
                        <i className="bi bi-calendar-event me-2"></i>
                        {new Date(game.date).toLocaleDateString()}
                      </p>
                    )}

                    {game.location && (
                      <p className="text-muted small mb-2">
                        <i className="bi bi-geo-alt me-2"></i>
                        {game.location}
                      </p>
                    )}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                    <small className="text-muted">
                      <i className="bi bi-clock me-1"></i>
                      Created: {new Date(game.createdAt).toLocaleDateString()}
                    </small>
                  </div>
                </div>

                <div className="card-footer bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-fill">
                      <small className="text-muted d-block">Game ID</small>
                      <code className="small">{game.id}</code>
                    </div>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGameClick(game.id);
                        }}
                        title="Open Dashboard"
                      >
                        <i className="bi bi-box-arrow-in-right"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditGame(game);
                        }}
                        title="Edit Game"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGame(
                            game.id,
                            game.gameName,
                            game?.details?.file
                          );
                        }}
                        title="Delete Game"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <style jsx>{`
        .hover-shadow:hover {
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
          transform: translateY(-2px);
        }
      `}</style>

      <Popup
        isOpen={popup.isOpen}
        title={popup.title}
        message={popup.message}
        type={popup.type}
        onClose={() => setPopup({ ...popup, isOpen: false })}
      />

      <Confirm
        isOpen={confirm.isOpen}
        title={confirm.title}
        message={confirm.message}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ ...confirm, isOpen: false })}
        type={confirm.type}
      />
    </div>
  );
};

export default OrganizerHome;
